import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../Store";
import { clearTraining } from "./TrainingSlice";

export enum SelectedTab {
  Timeline = 0,
  ActorPresets = 1,
  PropPresets = 2,
  ProjectSettings = 3,
}

export enum CollapsableColumn {
  Search = 0,
  ErrorLog = 1,
}

export type ThemeMode = "light" | "dark";

/**
 * How the browse dialogs (interaction picker, prop library, file explorer)
 * render their results. One preference for all three rather than one each —
 * the shell and every keystroke are identical between them, so a per-dialog
 * split would be a setting nobody asked for. Easy to widen into a record later
 * if that turns out to be wrong.
 */
export type BrowseDialogViewMode = "tile" | "list";
export type SequencePositionPickerType =
  | "actor"
  | "camera"
  | "prop"
  | "proximity"
  | "lookatTarget"
  | "takeLocation";
export type SequencePositionPickerPresentation = "splitView";

export interface SequencePositionPickerState {
  isOpen: boolean;
  nodeId: string;
  positionType: SequencePositionPickerType;
  title: string;
  presentation: SequencePositionPickerPresentation;
  targetIndex?: number;
}

export interface SequenceCanvasViewportState {
  x: number;
  y: number;
  zoom: number;
}

export type SequenceCanvasCommandType =
  | "formatLayout"
  | "createTextbox"
  | "createCommentZone"
  | "copySelection"
  | "cutSelection"
  | "pasteSelection"
  | "duplicateSelection";

export interface SequenceCanvasCommandState {
  type: SequenceCanvasCommandType | null;
  requestId: number;
}

export interface ViewportMapFocusState {
  x: number;
  y: number;
  requestId: number;
}

/** Camera picking on the 3D map (take camera or a Set Camera interaction). */
export interface CameraPickingState {
  /** Bumped to ask the map to fit the location's cameras again. */
  fitRequestId: number;
}

export interface SequenceCanvasSelectionState {
  nodeCount: number;
  /** The selected node when exactly one is selected (e.g. for the map's state at that node). */
  singleNodeId?: string;
  commentZoneCount: number;
  textboxCount: number;
  cuttableCount?: number;
}

export interface QuizFeedbackPanelState {
  isOpen: boolean;
  parentQuizId: string;
}

export interface GlobalState {
  isLoading: boolean;
  isPropertiesColumnOpen: boolean;
  isMenuExpanded: boolean;
  selectedTab: SelectedTab;
  showEnvironmentProps: boolean;
  /**
   * Take locations the user unfolded on the 3D map. Every location other than
   * the selected take's own is folded into one pin by default; this resets
   * whenever another take (or location) is selected.
   */
  expandedTakeLocationIds: string[];
  themeMode: ThemeMode;
  browseDialogViewMode: BrowseDialogViewMode;
  autoConnectToUnity: boolean;
  autoSave: boolean;
  isPropLibraryOpen: boolean;
  isCollapsableColumnOpen: boolean;
  selectedCollapsableColumn: CollapsableColumn;
  propLibraryTargetPosition: string;
  isQuickActorModalOpen: boolean;
  quickActorModalTargetPosition: string;
  focusedSequenceNodeId: string | null;
  sequencePositionPicker: SequencePositionPickerState;
  /** The map shown in place of the sequence canvas to look at a point, without picking. */
  isSequenceMapPeekOpen: boolean;
  /** The node the map below the sequence was last picking for: it shows the state after it. */
  sequenceMapPeekNodeId: string | null;
  /** The map below the sequence was open when the user last left it: reopen it with the next sequence. */
  isSequenceMapRemembered: boolean;
  sequenceCanvasViewports: Record<string, SequenceCanvasViewportState>;
  shouldRestoreSequenceCanvasViewport: boolean;
  sequenceCanvasCommand: SequenceCanvasCommandState;
  viewportMapFocus: ViewportMapFocusState;
  cameraPicking: CameraPickingState;
  sequenceCanvasSelection: SequenceCanvasSelectionState;
  isAdvancedSearchOpen: boolean;
  isValidationOverlayOpen: boolean;
  quizFeedback: QuizFeedbackPanelState;
  /**
   * The slide being edited in the selected PowerSlide. Shared rather than local
   * because the rail, the canvas and keyboard navigation all move it. A stale id
   * (after switching interactions, or deleting the slide) is harmless: the
   * editor falls back to the deck's first slide.
   */
  selectedSlideId: string;
  advancedTranslation: AdvancedTranslationState;
}

/** Fill only the empty texts, or replace every text. */
export type TranslationScope = "missing" | "all";

/**
 * The interaction properties column's advanced translation mode.
 *
 * Kept here rather than in the column so it survives moving between
 * interactions — editors work through a run of them in one sitting, and
 * re-picking languages each time made the mode not worth switching on. It is
 * session state, not document state: never serialised into the training, and
 * cleared whenever a training is torn down.
 *
 * `from` and `to` are null until the editor actually changes them, so an
 * untouched panel keeps tracking the training's reference language and language
 * list rather than freezing a snapshot of them.
 */
export interface AdvancedTranslationState {
  isEnabled: boolean;
  from: string | null;
  to: string[] | null;
  scope: TranslationScope;
}

const defaultAdvancedTranslation: AdvancedTranslationState = {
  isEnabled: false,
  from: null,
  to: null,
  scope: "missing",
};

const defaultSequencePositionPickerState: SequencePositionPickerState = {
  isOpen: false,
  nodeId: "",
  positionType: "actor",
  title: "",
  presentation: "splitView",
};

const getInitialThemeMode = (): ThemeMode => {
  const stored = localStorage.getItem("themeMode");
  if (stored === "light" || stored === "dark") {
    return stored;
  }
  // Default to light mode, or could check system preference: window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  return "light";
};

export const BROWSE_DIALOG_VIEW_MODE_STORAGE_KEY = "browseDialogViewMode";

const getInitialBrowseDialogViewMode = (): BrowseDialogViewMode => {
  const stored = localStorage.getItem(BROWSE_DIALOG_VIEW_MODE_STORAGE_KEY);
  if (stored === "tile" || stored === "list") {
    return stored;
  }
  return "tile";
};

const getInitialAutoConnectToUnity = (): boolean => {
  return localStorage.getItem("autoConnectToUnity") === "true";
};

const getInitialAutoSave = (): boolean => {
  const stored = localStorage.getItem("autoSave");
  if (stored === "false") {
    return false;
  }
  return true;
};

const initialState: GlobalState = {
  isLoading: false,
  isPropertiesColumnOpen: true,
  isMenuExpanded: true,
  selectedTab: SelectedTab.Timeline,
  showEnvironmentProps: localStorage.getItem("showEnvironmentProps") === "true",
  expandedTakeLocationIds: [],
  themeMode: getInitialThemeMode(),
  browseDialogViewMode: getInitialBrowseDialogViewMode(),
  autoConnectToUnity: getInitialAutoConnectToUnity(),
  autoSave: getInitialAutoSave(),
  isPropLibraryOpen: false,
  isCollapsableColumnOpen: false,
  selectedCollapsableColumn: CollapsableColumn.Search,
  propLibraryTargetPosition: "",
  isQuickActorModalOpen: false,
  quickActorModalTargetPosition: "",
  focusedSequenceNodeId: null,
  sequencePositionPicker: defaultSequencePositionPickerState,
  isSequenceMapPeekOpen: false,
  sequenceMapPeekNodeId: null,
  isSequenceMapRemembered: localStorage.getItem("sequenceMapOpen") === "true",
  sequenceCanvasViewports: {},
  shouldRestoreSequenceCanvasViewport: false,
  sequenceCanvasCommand: {
    type: null,
    requestId: 0,
  },
  viewportMapFocus: {
    x: 0,
    y: 0,
    requestId: 0,
  },
  cameraPicking: {
    fitRequestId: 0,
  },
  sequenceCanvasSelection: {
    nodeCount: 0,
    commentZoneCount: 0,
    textboxCount: 0,
    cuttableCount: 0,
  },
  isAdvancedSearchOpen: false,
  isValidationOverlayOpen: false,
  quizFeedback: {
    isOpen: false,
    parentQuizId: "",
  },
  selectedSlideId: "",
  advancedTranslation: defaultAdvancedTranslation,
};

export const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsPropertiesColumnOpen: (state, action: PayloadAction<boolean>) => {
      state.isPropertiesColumnOpen = action.payload;
    },
    setIsMenuExpanded: (state, action: PayloadAction<boolean>) => {
      state.isMenuExpanded = action.payload;
    },
    setSelectedTab: (state, action: PayloadAction<SelectedTab>) => {
      state.selectedTab = action.payload;
      if (action.payload !== SelectedTab.Timeline) {
        state.sequencePositionPicker = defaultSequencePositionPickerState;
      }
    },
    setShowEnvironmentProps: (state, action: PayloadAction<boolean>) => {
      state.showEnvironmentProps = action.payload;
    },
    toggleTakeLocationExpanded: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.expandedTakeLocationIds = state.expandedTakeLocationIds.includes(id)
        ? state.expandedTakeLocationIds.filter((other) => other !== id)
        : [...state.expandedTakeLocationIds, id];
    },
    setExpandedTakeLocationIds: (state, action: PayloadAction<string[]>) => {
      state.expandedTakeLocationIds = action.payload;
    },
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.themeMode = action.payload;
    },
    setBrowseDialogViewMode: (
      state,
      action: PayloadAction<BrowseDialogViewMode>
    ) => {
      state.browseDialogViewMode = action.payload;
    },
    setAutoConnectToUnity: (state, action: PayloadAction<boolean>) => {
      state.autoConnectToUnity = action.payload;
    },
    setAutoSave: (state, action: PayloadAction<boolean>) => {
      state.autoSave = action.payload;
    },
    applyUserPreferences: (
      state,
      action: PayloadAction<{ darkMode?: boolean; autoSave?: boolean }>
    ) => {
      if (typeof action.payload.darkMode === "boolean") {
        state.themeMode = action.payload.darkMode ? "dark" : "light";
      }
      if (typeof action.payload.autoSave === "boolean") {
        state.autoSave = action.payload.autoSave;
      }
    },
    setIsPropLibraryOpen: (
      state,
      action: PayloadAction<{ open: boolean; positionId: string }>
    ) => {
      state.isPropLibraryOpen = action.payload.open;
      state.propLibraryTargetPosition = action.payload.positionId;
    },
    setIsCollapsableColumnOpen: (
      state,
      action: PayloadAction<{ open: boolean; column: CollapsableColumn }>
    ) => {
      state.isCollapsableColumnOpen = action.payload.open;
      state.selectedCollapsableColumn = action.payload.column;
    },
    setPropLibraryTargetPosition: (state, action: PayloadAction<string>) => {
      state.propLibraryTargetPosition = action.payload;
    },
    setIsQuickActorModalOpen: (
      state,
      action: PayloadAction<{ open: boolean; positionId: string }>
    ) => {
      state.isQuickActorModalOpen = action.payload.open;
      state.quickActorModalTargetPosition = action.payload.positionId;
    },
    setFocusedSequenceNodeId: (state, action: PayloadAction<string | null>) => {
      state.focusedSequenceNodeId = action.payload;
    },
    openSequencePositionPicker: (
      state,
      action: PayloadAction<{
        nodeId: string;
        positionType: SequencePositionPickerType;
        title: string;
        presentation?: SequencePositionPickerPresentation;
        targetIndex?: number;
      }>
    ) => {
      state.sequencePositionPicker = {
        isOpen: true,
        nodeId: action.payload.nodeId,
        positionType: action.payload.positionType,
        title: action.payload.title,
        presentation: action.payload.presentation ?? "splitView",
        targetIndex: action.payload.targetIndex,
      };
      // One map below the sequence at a time.
      state.isSequenceMapPeekOpen = false;
      state.shouldRestoreSequenceCanvasViewport = false;
    },
    closeSequencePositionPicker: (state) => {
      // A picker below the sequence leaves its map open once a position is
      // picked; cancelling closes both (see Viewport3D).
      if (
        state.sequencePositionPicker.isOpen &&
        state.sequencePositionPicker.positionType !== "takeLocation"
      ) {
        state.isSequenceMapPeekOpen = true;
        state.sequenceMapPeekNodeId = state.sequencePositionPicker.nodeId;
        state.isSequenceMapRemembered = true;
      }
      state.sequencePositionPicker = defaultSequencePositionPickerState;
      state.shouldRestoreSequenceCanvasViewport = true;
    },
    openSequenceMapPeek: (
      state,
      // Without a point the map keeps its view (opened by dragging the split up).
      action: PayloadAction<{ x: number; y: number } | undefined>
    ) => {
      state.isSequenceMapPeekOpen = true;
      state.sequenceMapPeekNodeId = null;
      state.isSequenceMapRemembered = true;
      state.sequencePositionPicker = defaultSequencePositionPickerState;
      if (!action.payload) return;
      state.viewportMapFocus = {
        x: action.payload.x,
        y: action.payload.y,
        requestId: state.viewportMapFocus.requestId + 1,
      };
    },
    closeSequenceMapPeek: (state) => {
      state.isSequenceMapPeekOpen = false;
      state.sequenceMapPeekNodeId = null;
    },
    setSequenceMapRemembered: (state, action: PayloadAction<boolean>) => {
      state.isSequenceMapRemembered = action.payload;
    },
    setSequenceCanvasViewport: (
      state,
      action: PayloadAction<{
        sequenceId: string;
        viewport: SequenceCanvasViewportState;
      }>
    ) => {
      state.sequenceCanvasViewports[action.payload.sequenceId] =
        action.payload.viewport;
    },
    clearSequenceCanvasViewportRestore: (state) => {
      state.shouldRestoreSequenceCanvasViewport = false;
    },
    queueSequenceCanvasCommand: (
      state,
      action: PayloadAction<SequenceCanvasCommandType>
    ) => {
      state.sequenceCanvasCommand = {
        type: action.payload,
        requestId: state.sequenceCanvasCommand.requestId + 1,
      };
    },
    clearSequenceCanvasCommand: (state) => {
      state.sequenceCanvasCommand = {
        ...state.sequenceCanvasCommand,
        type: null,
      };
    },
    requestViewportMapFocus: (
      state,
      action: PayloadAction<{ x: number; y: number }>
    ) => {
      state.viewportMapFocus = {
        x: action.payload.x,
        y: action.payload.y,
        requestId: state.viewportMapFocus.requestId + 1,
      };
    },
    requestCameraPickingFit: (state) => {
      state.cameraPicking.fitRequestId += 1;
    },
    setSequenceCanvasSelection: (
      state,
      action: PayloadAction<SequenceCanvasSelectionState>
    ) => {
      // Selecting something else (or nothing) on the canvas: the map no longer
      // shows the state at the node last picked for.
      const previous = state.sequenceCanvasSelection;
      if (
        previous.singleNodeId !== action.payload.singleNodeId ||
        previous.nodeCount !== action.payload.nodeCount
      )
        state.sequenceMapPeekNodeId = null;
      state.sequenceCanvasSelection = action.payload;
    },
    setIsAdvancedSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.isAdvancedSearchOpen = action.payload;
      if (action.payload) state.isValidationOverlayOpen = false;
    },
    setIsValidationOverlayOpen: (state, action: PayloadAction<boolean>) => {
      state.isValidationOverlayOpen = action.payload;
      if (action.payload) state.isAdvancedSearchOpen = false;
    },
    openQuizFeedback: (state, action: PayloadAction<string>) => {
      state.quizFeedback = {
        isOpen: true,
        parentQuizId: action.payload,
      };
    },
    closeQuizFeedback: (state) => {
      state.quizFeedback = {
        isOpen: false,
        parentQuizId: "",
      };
    },
    setSelectedSlideId: (state, action: PayloadAction<string>) => {
      state.selectedSlideId = action.payload;
    },
    setAdvancedTranslationEnabled: (state, action: PayloadAction<boolean>) => {
      state.advancedTranslation.isEnabled = action.payload;
    },
    setAdvancedTranslationSelection: (
      state,
      action: PayloadAction<{
        from: string;
        to: string[];
        scope: TranslationScope;
      }>
    ) => {
      state.advancedTranslation = {
        ...state.advancedTranslation,
        ...action.payload,
      };
    },
    resetAdvancedTranslation: (state) => {
      state.advancedTranslation = defaultAdvancedTranslation;
    },
  },
  extraReducers: (builder) => {
    // Every training teardown clears it: leaving the editor, and the
    // clearTraining that runs at the start of loading another one. The chosen
    // languages belong to the training that was open, so they must not follow
    // the editor into the next one.
    builder.addCase(clearTraining, (state) => {
      state.advancedTranslation = defaultAdvancedTranslation;
      state.expandedTakeLocationIds = [];
    });
  },
});

// reducers
export const {
  setIsLoading,
  setIsPropertiesColumnOpen,
  setIsMenuExpanded,
  setSelectedTab,
  setShowEnvironmentProps,
  toggleTakeLocationExpanded,
  setExpandedTakeLocationIds,
  setThemeMode,
  setBrowseDialogViewMode,
  setAutoConnectToUnity,
  setAutoSave,
  applyUserPreferences,
  setIsPropLibraryOpen,
  setIsCollapsableColumnOpen,
  setPropLibraryTargetPosition,
  setIsQuickActorModalOpen,
  setFocusedSequenceNodeId,
  openSequencePositionPicker,
  closeSequencePositionPicker,
  openSequenceMapPeek,
  closeSequenceMapPeek,
  setSequenceMapRemembered,
  setSequenceCanvasViewport,
  clearSequenceCanvasViewportRestore,
  queueSequenceCanvasCommand,
  clearSequenceCanvasCommand,
  requestViewportMapFocus,
  requestCameraPickingFit,
  setSequenceCanvasSelection,
  setIsAdvancedSearchOpen,
  setIsValidationOverlayOpen,
  openQuizFeedback,
  closeQuizFeedback,
  setSelectedSlideId,
  setAdvancedTranslationEnabled,
  setAdvancedTranslationSelection,
  resetAdvancedTranslation,
} = globalSlice.actions;

export const selectIsLoading = (state: RootState) => state.global.isLoading;
export const selectIsPropertiesColumnOpen = (state: RootState) =>
  state.global.isPropertiesColumnOpen;
export const selectIsMenuExpanded = (state: RootState) =>
  state.global.isMenuExpanded;
export const selectSelectedTab = (state: RootState) => state.global.selectedTab;
export const selectShowEnvironmentProps = (state: RootState) =>
  state.global.showEnvironmentProps;
export const selectExpandedTakeLocationIds = (state: RootState) =>
  state.global.expandedTakeLocationIds;
export const selectThemeMode = (state: RootState) => state.global.themeMode;
export const selectBrowseDialogViewMode = (state: RootState) =>
  state.global.browseDialogViewMode ?? "tile";
export const selectAutoConnectToUnity = (state: RootState) =>
  state.global.autoConnectToUnity;
export const selectAutoSave = (state: RootState) => state.global.autoSave;
export const selectIsPropLibraryOpen = (state: RootState) =>
  state.global.isPropLibraryOpen;
export const selectIsCollapsableColumnOpen = (state: RootState) =>
  state.global.isCollapsableColumnOpen;
export const selectSelectedCollapsableColumn = (state: RootState) =>
  state.global.selectedCollapsableColumn;
export const selectPropLibraryTargetPosition = (state: RootState) =>
  state.global.propLibraryTargetPosition;
export const selectIsQuickActorModalOpen = (state: RootState) =>
  state.global.isQuickActorModalOpen;
export const selectQuickActorModalTargetPosition = (state: RootState) =>
  state.global.quickActorModalTargetPosition;
export const selectFocusedSequenceNodeId = (state: RootState) =>
  state.global.focusedSequenceNodeId;
export const selectSequencePositionPicker = (state: RootState) =>
  state.global.sequencePositionPicker;
export const selectIsSequencePositionPickerOpen = (state: RootState) =>
  state.global.sequencePositionPicker.isOpen;
export const selectIsSequenceMapPeekOpen = (state: RootState) =>
  state.global.isSequenceMapPeekOpen;
export const selectIsSequenceMapRemembered = (state: RootState) =>
  state.global.isSequenceMapRemembered;
export const selectSequenceMapPeekNodeId = (state: RootState) =>
  state.global.sequenceMapPeekNodeId;
export const selectSequenceCanvasViewport = (
  state: RootState,
  sequenceId: string,
) => state.global.sequenceCanvasViewports[sequenceId] ?? null;
export const selectShouldRestoreSequenceCanvasViewport = (state: RootState) =>
  state.global.shouldRestoreSequenceCanvasViewport;
export const selectSequenceCanvasCommand = (state: RootState) =>
  state.global.sequenceCanvasCommand;
export const selectViewportMapFocus = (state: RootState) =>
  state.global.viewportMapFocus;
export const selectCameraPickingFitRequestId = (state: RootState) =>
  state.global.cameraPicking.fitRequestId;
export const selectSequenceCanvasSelection = (state: RootState) =>
  state.global.sequenceCanvasSelection;
export const selectIsAdvancedSearchOpen = (state: RootState) =>
  state.global.isAdvancedSearchOpen;
export const selectIsValidationOverlayOpen = (state: RootState) =>
  state.global.isValidationOverlayOpen;
export const selectIsQuizFeedbackOpen = (state: RootState) =>
  state.global.quizFeedback?.isOpen ?? false;
export const selectQuizFeedbackParentQuizId = (state: RootState) =>
  state.global.quizFeedback?.parentQuizId ?? "";
export const selectSelectedSlideId = (state: RootState) =>
  state.global.selectedSlideId ?? "";

export const selectAdvancedTranslation = (state: RootState) =>
  state.global.advancedTranslation ?? defaultAdvancedTranslation;

export default globalSlice.reducer;
